import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, CheckCircle2, XCircle, AlertCircle,
  Search, Palmtree, Heart, Briefcase, Baby,
  User, Building2, RotateCcw, MessageSquare, Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { db } from '../../../lib/db';
import { formatDate } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

const leaveTypeInfo = {
  annual: { label: 'Annual Leave', icon: Palmtree, color: 'bg-blue-100 text-blue-600' },
  sick: { label: 'Sick Leave', icon: Heart, color: 'bg-red-100 text-red-600' },
  casual: { label: 'Casual Leave', icon: Briefcase, color: 'bg-amber-100 text-amber-600' },
  maternity: { label: 'Maternity Leave', icon: Baby, color: 'bg-pink-100 text-pink-600' },
  paternity: { label: 'Paternity Leave', icon: Baby, color: 'bg-cyan-100 text-cyan-600' },
  compensatory: { label: 'Comp Off', icon: Clock, color: 'bg-purple-100 text-purple-600' }
};

export default function LeaveReview() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [comments, setComments] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, requestsData] = await Promise.all([
        db.users.toArray(),
        db.leaveRequests.toArray()
      ]);
      
      setAllUsers(usersData);
      
      // Get leave requests that CEO should review:
      // 1. Supervisor leave requests (pending)
      // 2. Escalated employee requests (pending_ceo)
      const ceoLeaveRequests = requestsData.filter(r => {
        const requestor = usersData.find(u => u.id === r.userId);
        return r.status === 'pending_ceo' || 
          (r.status === 'pending' && requestor?.role === 'supervisor');
      });
      
      // Enrich with user data
      const enriched = ceoLeaveRequests.map(r => ({
        ...r,
        employee: usersData.find(u => u.id === r.userId)
      })).sort((a, b) => b.createdAt - a.createdAt);
      
      setLeaveRequests(enriched);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const departments = [...new Set(allUsers.map(u => u.profile?.department).filter(Boolean))];

  const filteredRequests = leaveRequests.filter(r => {
    const matchesSearch = searchQuery === '' ||
      r.employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesStatus = false;
    if (statusFilter === 'all') {
      matchesStatus = true;
    } else if (statusFilter === 'pending') {
      matchesStatus = r.status === 'pending' || r.status === 'pending_ceo';
    } else if (statusFilter === 'supervisor_pending') {
      matchesStatus = r.status === 'pending' && r.employee?.role === 'supervisor';
    } else if (statusFilter === 'escalated') {
      matchesStatus = r.status === 'pending_ceo';
    } else {
      matchesStatus = r.status === statusFilter;
    }
    
    const matchesDept = departmentFilter === 'all' || 
      r.employee?.profile?.department === departmentFilter;
    
    return matchesSearch && matchesStatus && matchesDept;
  });

  const pendingCount = leaveRequests.filter(r => r.status === 'pending' || r.status === 'pending_ceo').length;
  const supervisorPendingCount = leaveRequests.filter(r => {
    return r.status === 'pending' && r.employee?.role === 'supervisor';
  }).length;
  const escalatedCount = leaveRequests.filter(r => r.status === 'pending_ceo').length;
  const totalPendingDays = leaveRequests
    .filter(r => r.status === 'pending' || r.status === 'pending_ceo')
    .reduce((sum, r) => sum + (r.days || 0), 0);

  const getEmployeeName = (userId) => {
    const member = allUsers.find(u => u.id === userId);
    return member ? `${member.profile?.firstName} ${member.profile?.lastName}` : 'Unknown';
  };

  const getForwarderName = (forwardedBy) => {
    const forwarder = allUsers.find(u => u.id === forwardedBy);
    return forwarder ? `${forwarder.profile?.firstName} ${forwarder.profile?.lastName}` : 'Unknown';
  };

  const handleAction = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setComments('');
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;

    try {
      let updateData = {};
      
      if (actionType === 'approve') {
        updateData = {
          status: 'approved',
          approvedBy: user.id,
          ceoApprovedBy: user.id,
          ceoReviewedAt: Date.now(),
          ceoComments: comments,
          updatedAt: Date.now()
        };

        // Update leave balance
        const leaveType = selectedRequest.leaveType;
        const balance = await db.leaveBalances.where('userId').equals(selectedRequest.userId).first();
        
        if (balance && balance[leaveType]) {
          await db.leaveBalances.where('userId').equals(selectedRequest.userId).modify({
            [`${leaveType}.used`]: (balance[leaveType].used || 0) + selectedRequest.days,
            [`${leaveType}.pending`]: Math.max(0, (balance[leaveType].pending || 0) - selectedRequest.days)
          });
        }
        
        toast.success('Leave request approved');
      } else if (actionType === 'reject') {
        updateData = {
          status: 'rejected',
          rejectedBy: user.id,
          ceoRejectedBy: user.id,
          ceoReviewedAt: Date.now(),
          ceoComments: comments,
          rejectionReason: comments,
          updatedAt: Date.now()
        };

        // Restore pending balance
        const leaveType = selectedRequest.leaveType;
        const balance = await db.leaveBalances.where('userId').equals(selectedRequest.userId).first();
        
        if (balance && balance[leaveType]) {
          await db.leaveBalances.where('userId').equals(selectedRequest.userId).modify({
            [`${leaveType}.pending`]: Math.max(0, (balance[leaveType].pending || 0) - selectedRequest.days)
          });
        }
        
        toast.success('Leave request rejected');
      } else if (actionType === 'send_back') {
        // Send back to supervisor for review
        updateData = {
          status: 'pending',
          sentBackBy: user.id,
          sentBackAt: Date.now(),
          ceoComments: comments,
          updatedAt: Date.now()
        };
        
        toast.success('Leave request sent back to supervisor');
      }

      await db.leaveRequests.update(selectedRequest.id, updateData);
      setShowActionModal(false);
      loadData();
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error('Failed to update request');
    }
  };

  const getStatusBadge = (request) => {
    if (request.status === 'approved') {
      return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>;
    } else if (request.status === 'rejected') {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
    } else if (request.status === 'pending_ceo') {
      return <Badge className="bg-purple-100 text-purple-700"><Send className="w-3 h-3 mr-1" />Escalated</Badge>;
    } else if (request.sentBackBy) {
      return <Badge className="bg-orange-100 text-orange-700"><RotateCcw className="w-3 h-3 mr-1" />Returned</Badge>;
    } else {
      return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Review</h1>
          <p className="text-gray-500 mt-1">Review supervisor and escalated leave requests</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-amber-100 text-amber-700 px-3 py-1 text-sm">
            <AlertCircle className="w-4 h-4 mr-2" />
            {pendingCount} Awaiting Your Review
          </Badge>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100">Total Pending</p>
                <p className="text-3xl font-bold mt-1">{pendingCount}</p>
              </div>
              <Calendar className="w-8 h-8 text-white/80" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Supervisor Requests</p>
                <p className="text-2xl font-bold text-teal-600">{supervisorPendingCount}</p>
              </div>
              <User className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Escalated</p>
                <p className="text-2xl font-bold text-purple-600">{escalatedCount}</p>
              </div>
              <Send className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Days</p>
                <p className="text-2xl font-bold text-amber-600">{totalPendingDays}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by employee name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">All Pending</SelectItem>
                <SelectItem value="supervisor_pending">Supervisor Requests</SelectItem>
                <SelectItem value="escalated">Escalated by Supervisor</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leave Requests */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Leave Requests</CardTitle>
          <CardDescription>
            {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No leave requests to review</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map(request => {
                const typeInfo = leaveTypeInfo[request.leaveType] || leaveTypeInfo.casual;
                const TypeIcon = typeInfo.icon;
                const isPending = request.status === 'pending' || request.status === 'pending_ceo';
                
                return (
                  <div key={request.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                          {request.employee?.profile?.firstName?.[0]}
                          {request.employee?.profile?.lastName?.[0]}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900">
                              {request.employee?.profile?.firstName} {request.employee?.profile?.lastName}
                            </p>
                            {request.employee?.role === 'supervisor' && (
                              <Badge variant="outline" className="text-xs">Supervisor</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {request.employee?.profile?.department} • {request.employee?.profile?.position}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge className={typeInfo.color}>
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {typeInfo.label}
                            </Badge>
                            <span className="text-sm text-gray-600">
                              {formatDate(request.startDate)} - {formatDate(request.endDate)}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {request.days} day{request.days !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col md:items-end gap-2">
                        {getStatusBadge(request)}
                        {isPending && (
                          <div className="flex gap-2">
                            {request.status === 'pending_ceo' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                onClick={() => handleAction(request, 'send_back')}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Return
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleAction(request, 'reject')}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleAction(request, 'approve')}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {request.reason && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Reason: </span>
                          {request.reason}
                        </p>
                      </div>
                    )}

                    {request.status === 'pending_ceo' && request.forwardedBy && (
                      <div className="mt-2 p-2 bg-purple-50 rounded text-sm">
                        <span className="font-medium text-purple-700">Escalated by: </span>
                        {getForwarderName(request.forwardedBy)}
                        {request.supervisorComments && (
                          <p className="mt-1 text-gray-600">{request.supervisorComments}</p>
                        )}
                      </div>
                    )}

                    {request.ceoComments && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                        <span className="font-medium">CEO Comments: </span>
                        {request.ceoComments}
                      </div>
                    )}

                    {request.rejectionReason && request.status === 'rejected' && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-sm">
                        <span className="font-medium text-red-700">Rejection Reason: </span>
                        {request.rejectionReason}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' && 'Approve Leave Request'}
              {actionType === 'reject' && 'Reject Leave Request'}
              {actionType === 'send_back' && 'Return to Supervisor'}
            </DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <span>
                  {selectedRequest.employee?.profile?.firstName} {selectedRequest.employee?.profile?.lastName} - 
                  {' '}{selectedRequest.days} day{selectedRequest.days !== 1 ? 's' : ''} 
                  {' '}({leaveTypeInfo[selectedRequest.leaveType]?.label || 'Leave'})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="comments">
                {actionType === 'reject' ? 'Rejection Reason' : 'Comments'} 
                {actionType === 'reject' && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Textarea
                id="comments"
                placeholder={
                  actionType === 'approve' 
                    ? "Add any notes (optional)..." 
                    : actionType === 'reject'
                    ? "Please provide a reason for rejection..."
                    : "Add notes for the supervisor..."
                }
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmAction}
              disabled={actionType === 'reject' && !comments.trim()}
              className={
                actionType === 'approve' 
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : actionType === 'reject'
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-orange-600 hover:bg-orange-700 text-white"
              }
            >
              {actionType === 'approve' && (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve Request
                </>
              )}
              {actionType === 'reject' && (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Request
                </>
              )}
              {actionType === 'send_back' && (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Return to Supervisor
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
